import type { FC, ReactNode } from 'react'
import type { PixelPalette } from '@/lib/pixel'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { getRowsSize, rowsToLayers } from '@/lib/pixel'

import { INK_PALETTE } from '@/constants/sprites'

interface PixelPathsProps {
    rows: ReadonlyArray<string>
    palette?: PixelPalette
}

interface PixelSpriteProps extends PixelPathsProps {
    className?: string
    label?: string
}

export interface PixelStackLayer extends PixelPathsProps {
    className?: string
}

interface PixelStackProps {
    layers: ReadonlyArray<PixelStackLayer>
    className?: string
    label?: string
}

/** Paths only, for composing several sprites inside one svg. */
export const PixelPaths: FC<PixelPathsProps> = ({ rows, palette = INK_PALETTE }): ReactNode => {
    const layers = useMemo(() => rowsToLayers(rows, palette), [rows, palette])

    return layers.map(layer => (
        <path key={layer.fill} d={layer.d} style={{ fill: layer.fill }} />
    ))
}

/** A single pixel-art sprite drawn from rows of characters. Decorative unless a label is given. */
export const PixelSprite: FC<PixelSpriteProps> = ({
    rows,
    palette = INK_PALETTE,
    className,
    label
}): ReactNode => {
    const { width, height } = getRowsSize(rows)

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            shapeRendering='crispEdges'
            className={cn('block overflow-visible', className)}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable='false'
        >
            <PixelPaths rows={rows} palette={palette} />
        </svg>
    )
}

/** Several same-sized layers in one svg, each layer can carry its own animation class. */
export const PixelStack: FC<PixelStackProps> = ({ layers, className, label }): ReactNode => {
    const { width, height } = getRowsSize(layers[0]?.rows ?? [])

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            shapeRendering='crispEdges'
            className={cn('block overflow-visible', className)}
            role={label ? 'img' : undefined}
            aria-label={label}
            aria-hidden={label ? undefined : true}
            focusable='false'
        >
            {layers.map((layer, index) => (
                <g key={index} className={layer.className}>
                    <PixelPaths rows={layer.rows} palette={layer.palette} />
                </g>
            ))}
        </svg>
    )
}
